import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, Mail, Lock, ShieldAlert } from "lucide-react";
import { Logo } from "../components/Logo";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";

const BG = "https://images.unsplash.com/photo-1677129667171-92abd8740fa3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      nav(u.role === "admin" ? "/dashboard" : "/feedback");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={BG} alt="Tamanna hotel lobby" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/90 via-emerald-950/40 to-transparent" />
        <div className="absolute bottom-0 p-12 text-amber-50 max-w-xl rise">
          <div className="eyebrow !text-amber-300">Six properties · One guest voice</div>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.02] tracking-tight">Every guest story, heard and acted on.</h1>
          <p className="mt-4 text-amber-50/80 text-base">Capture feedback at the counter in seconds. Let AI turn voices into insights for management.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10">
        <form onSubmit={submit} className="w-full max-w-md glass rounded-3xl p-8 sm:p-10 rise d1" data-testid="login-form">
          <Logo />
          <h2 className="mt-8 text-3xl font-extrabold tracking-tight text-emerald-950">Sign in</h2>
          <p className="mt-1 text-sm text-slate-500">Staff and management accounts are created by your admin.</p>
          <label className="field-label mt-8 block">Email</label>
          <div className="relative mt-2">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 pl-10 rounded-xl bg-white" placeholder="you@tamannahotels.com" />
          </div>
          <label className="field-label mt-5 block">Password</label>
          <div className="relative mt-2">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 pl-10 rounded-xl bg-white" placeholder="••••••••" />
          </div>
          {error && (
            <div data-testid="login-error" className="mt-5 flex gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}
          <button data-testid="login-submit-button" disabled={busy} className="mt-8 w-full h-12 rounded-full bg-emerald-900 text-amber-50 font-semibold hover:bg-emerald-800 active:scale-[0.98] transition-[background-color,transform] disabled:opacity-60 flex items-center justify-center gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
          </button>
          <p className="mt-6 text-xs text-slate-500 leading-relaxed">New devices need management approval before first use. Management's first device is approved automatically.</p>
        </form>
      </div>
    </div>
  );
}
